/**
 * ============================
 * Custom Order Page 2 (オプション選択 / 確認) — 汎用オプション設計
 * ============================
 * ・ロード時に state を復元。productId 未設定なら Page 1 へリダイレクト
 * ・product.option_groups を順に描画（種別ハードコードなし）
 *     kind:"select" → カードグリッド（選択で state.selections[type] を更新）
 *     kind:"info"   → 情報カード（表示のみ）
 * ・pricing:"variant" の全軸が揃うと確認明細に価格を算出
 * ・カート追加は案C ダミー段階では Option B（非機能デモ）
 */

(function () {
  "use strict";

  const rootEl = document.getElementById("co-detail-app");
  if (!rootEl) return; // Page 2 以外では動かない

  const CO = window.CustomOrder;
  const state = CO.getState();

  // モデル未選択なら Page 1 へ戻す
  const product = CO.getProduct(state.productId);
  if (!product) {
    window.location.href = "/pages/custom-order";
    return;
  }

  const groupsEl = document.getElementById("co-option-groups");
  const confirmListEl = document.getElementById("co-confirm-list");
  const addCartBtn = document.getElementById("co-addcart");
  const hintEl = document.querySelector(".co-demo-hint");

  const optionGroups = product.option_groups || [];
  const UNSELECTED = "未選択";

  // ダミーデータの variant_id は 0。実データが入ると自動で false になるので、
  // Phase 1 の投入後にフラグを切り替える作業は要らない（切り替え忘れの防止）。
  const DEMO_MODE = !(product.variants || []).some((v) => v.variant_id);

  // O-8【決定済 2026-08-11】カートページで止める。チェックアウトへ直行させない。
  // 理由：カスタムオーダーと通常商品を一緒に買う導線を残すため。
  const CART_URL = "/cart";

  // 描画済みの select 軸。ある軸を選ぶと他の軸の受注可否が変わるため、
  // DOM を作り直さず状態だけ塗り替えられるよう参照を保持する
  // （作り直すとキーボードのフォーカスが飛ぶ）。
  const selectGroups = [];
  let groupSeq = 0;

  // カート追加のヒント文はテキストが差し替わるので読み上げ対象にする
  if (hintEl) hintEl.setAttribute("role", "status");

  /* ============================
     小物ユーティリティ
  ============================ */
  function setText(id, text) {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  function currentSelections() {
    return CO.getState().selections || {};
  }

  // 確認明細の文言は注文の line_item_properties にもそのまま載る（admin-schema §9-1）。
  // 画面と注文で文言がずれないよう、生成は core.js の1箇所に置いている。
  const groupLabel = CO.groupLabel;
  const choiceDisplayName = CO.choiceDisplayName;

  // カードのサブ表示（付帯情報 or 価格差）。無ければ空。
  // meta_label は管理画面で自由に入力する表示用ラベル（例「40cm」）で、
  // 単位や表記が軸ごとに違っても定義追加なしで扱える（admin-schema §3-3）。
  // extra_price は差額が実際にあるときだけ出す（0 で「+¥0」と出さない）。
  // カードのサブ表示。meta_label（「40cm」など）が最優先。
  // 差額は **その商品の実バリアント価格から算出**する（CO.choiceExtraPrice）。
  // choice.extra_price は選択肢共有の値で商品ごとの違いを表せないため使わない
  // （同じ革でも商品によって差額が 0 のことがある）。
  // 差額 0 のときはサブラベルを出さない（「+¥0」と書かない）。
  function choiceSubLabel(group, choice, choiceId, selections) {
    if (choice.meta_label) return choice.meta_label;
    const extra = CO.choiceExtraPrice(product, group, choiceId, selections);
    if (typeof extra === "number" && extra > 0) {
      return "+" + CO.formatYen(extra);
    }
    return "";
  }

  /* ============================
     選択軸（kind:"select"）の描画
  ============================ */
  function renderSelectGroup(group) {
    const wrap = document.createElement("div");
    wrap.className = "co-optgroup";

    const headingId = "co-optgroup-" + groupSeq++;
    const heading = document.createElement("h3");
    heading.className = "co-subtitle";
    heading.id = headingId;
    heading.textContent = groupLabel(group);
    wrap.appendChild(heading);

    const list = document.createElement("div");
    list.className = "co-grid";
    // 単一選択の軸なので radiogroup、カードが radio（A-8 / NFR6）
    list.setAttribute("role", "radiogroup");
    list.setAttribute("aria-labelledby", headingId);

    const typeDef = CO.getOptionType(group.type) || {};
    const choiceIds =
      group.choices && group.choices.length
        ? group.choices
        : Object.keys(typeDef.choices || {});

    const cards = [];

    choiceIds.forEach((cid) => {
      const choice = CO.getChoice(group.type, cid);
      if (!choice) return;

      const card = document.createElement("div");
      card.className = "co-card";
      card.setAttribute("data-id", cid);

      const sub = choiceSubLabel(group, choice, cid, currentSelections());
      const hasModal =
        (choice.modal_images && choice.modal_images.length) || choice.description;

      // 差額は他軸の選択で変わりうるので、サブラベルの器は常に用意しておく
      // （後から差額が出たときに DOM を作り直さず textContent だけ差し替える）。
      // meta_label は固定なので、その軸では器の出し入れは起きない。
      const mayShowSub = !!sub || group.pricing === "variant";

      // 名前しか出ないカード（サイズ等）は中央寄せにする
      if (!choice.thumbnail && !mayShowSub && !hasModal) {
        card.classList.add("co-card--textonly");
      }

      // 「詳細を見る」は radio の**外**に出す。role="radio" の内側にボタンを
      // 置くと支援技術からそのボタンを操作できなくなるため。
      // 管理画面由来のテキスト・URL は innerHTML に連結せず、
      // textContent / src プロパティで入れる（PRD §14 本番硬化）。
      card.innerHTML =
        '<div class="co-card__body" role="radio" tabindex="-1" aria-checked="false">' +
        (choice.thumbnail
          ? '<div class="co-card__imgwrap">' +
            '<img alt="" class="co-card__img" />' +
            "</div>"
          : "") +
        '<h3 class="co-card__title"></h3>' +
        (mayShowSub ? '<p class="co-card__price"></p>' : "") +
        "</div>" +
        (hasModal
          ? '<button type="button" class="co-btn-detail">詳細を見る</button>'
          : "");

      const imgEl = card.querySelector(".co-card__img");
      if (imgEl) imgEl.src = choice.thumbnail;
      card.querySelector(".co-card__title").textContent = choice.name;
      const subEl = card.querySelector(".co-card__price");
      if (subEl) subEl.textContent = sub;

      const entry = {
        id: cid,
        card: card,
        body: card.querySelector(".co-card__body"),
        subEl: subEl,
        choice: choice
      };
      cards.push(entry);

      const detailBtn = card.querySelector(".co-btn-detail");
      if (detailBtn) {
        detailBtn.addEventListener("click", () => {
          CO.openModal({
            images:
              choice.modal_images ||
              (choice.thumbnail ? [choice.thumbnail] : []),
            title: choice.name,
            // 開いた時点の選択状態で計算し直す（差額は他軸の選択で変わる）
            price: choiceSubLabel(group, choice, cid, currentSelections()),
            description: choice.description
          });
        });
      }

      // カードのどこを押しても選択。ただし「詳細を見る」は別動作。
      card.addEventListener("click", (e) => {
        if (e.target.closest(".co-btn-detail")) return;
        selectChoice(group, cid);
      });

      entry.body.addEventListener("keydown", (e) =>
        onRadioKeydown(e, group, cards, entry)
      );

      list.appendChild(card);
    });

    wrap.appendChild(list);
    selectGroups.push({ group: group, cards: cards });
    return wrap;
  }

  // radiogroup の標準キー操作：矢印で移動しその場で選択、Space / Enter で選択
  function onRadioKeydown(e, group, cards, entry) {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      selectChoice(group, entry.id);
      return;
    }

    const forward = e.key === "ArrowRight" || e.key === "ArrowDown";
    const backward = e.key === "ArrowLeft" || e.key === "ArrowUp";
    if (!forward && !backward) return;
    e.preventDefault();

    // 受注停止のカードは飛ばす（A-7）
    const selectable = cards.filter(
      (c) => !c.card.classList.contains("is-unavailable")
    );
    if (!selectable.length) return;

    let i = selectable.indexOf(entry);
    if (i === -1) i = forward ? -1 : 0; // 受注停止カードから移動した場合
    const next =
      selectable[(i + (forward ? 1 : -1) + selectable.length) % selectable.length];

    selectChoice(group, next.id);
    next.body.focus();
  }

  function selectChoice(group, choiceId) {
    // 受注停止の組合せは選ばせない（A-7）
    if (!CO.isChoiceAvailable(product, group, choiceId, currentSelections())) return;

    const sel = Object.assign({}, currentSelections());
    sel[group.type] = choiceId;
    CO.setState({ selections: sel });

    refreshSelectStates();
    renderConfirmation();
  }

  // 選択状態・受注可否・タブ順を全ての軸に反映する。
  // ある軸を選ぶと他の軸の受注可否が変わるので、毎回すべて塗り直す。
  function refreshSelectStates() {
    const sel = currentSelections();

    selectGroups.forEach((entry) => {
      const selectedId = sel[entry.group.type];

      entry.cards.forEach((c) => {
        const available = CO.isChoiceAvailable(product, entry.group, c.id, sel);
        const isSelected = selectedId === c.id;

        c.card.classList.toggle("is-selected", isSelected);
        c.card.classList.toggle("is-unavailable", !available);
        c.body.setAttribute("aria-checked", isSelected ? "true" : "false");

        // 差額は他軸の選択で変わるので、状態更新のたびに引き直す
        if (c.subEl) {
          c.subEl.textContent = choiceSubLabel(entry.group, c.choice, c.id, sel);
        }
        // 「押せないが読み上げはされる」状態にする（存在自体は伝える）
        if (available) c.body.removeAttribute("aria-disabled");
        else c.body.setAttribute("aria-disabled", "true");
      });

      // radiogroup 内の Tab 到達点は1つだけ（roving tabindex）。
      // 選択済みがあればそれ、無ければ最初の選択可能なカード。
      const tabStop =
        entry.cards.find((c) => c.id === selectedId) ||
        entry.cards.find((c) => !c.card.classList.contains("is-unavailable")) ||
        entry.cards[0];
      entry.cards.forEach((c) => {
        c.body.setAttribute("tabindex", c === tabStop ? "0" : "-1");
      });
    });
  }

  /* ============================
     情報軸（kind:"info"）の描画（表示のみ）
  ============================ */
  function renderInfoGroup(group) {
    const wrap = document.createElement("div");
    wrap.className = "co-optgroup";

    const heading = document.createElement("h3");
    heading.className = "co-subtitle";
    heading.textContent = group.label || "仕様";
    wrap.appendChild(heading);

    const listEl = document.createElement("ul");
    listEl.className = "co-spec";
    (group.items || []).forEach((item) => {
      const li = document.createElement("li");
      li.className = "co-spec__row";
      const label = document.createElement("span");
      label.className = "co-spec__label";
      label.textContent = item.label;
      const value = document.createElement("span");
      value.className = "co-spec__value";
      value.textContent = item.value;
      li.appendChild(label);
      li.appendChild(value);
      listEl.appendChild(li);
    });
    wrap.appendChild(listEl);
    return wrap;
  }

  function renderGroups() {
    groupsEl.innerHTML = "";
    selectGroups.length = 0;
    optionGroups.forEach((group) => {
      if (group.kind === "info") {
        groupsEl.appendChild(renderInfoGroup(group));
      } else {
        groupsEl.appendChild(renderSelectGroup(group));
      }
    });
    refreshSelectStates();
  }

  /* ============================
     確認・明細
  ============================ */
  function confirmRow(label, value) {
    const li = document.createElement("li");
    li.className = "co-confirm__row";
    const labelEl = document.createElement("span");
    labelEl.className = "co-confirm__label";
    labelEl.textContent = label;
    const valueEl = document.createElement("span");
    valueEl.className = "co-confirm__value";
    valueEl.textContent = value;
    li.appendChild(labelEl);
    li.appendChild(valueEl);
    return li;
  }

  function renderConfirmation() {
    const sel = currentSelections();

    confirmListEl.innerHTML = "";
    confirmListEl.appendChild(confirmRow("モデル", product.name));

    optionGroups.forEach((group) => {
      if (group.kind === "info") return; // 情報軸は明細に出さない
      const cid = sel[group.type];
      const choice = cid ? CO.getChoice(group.type, cid) : null;
      confirmListEl.appendChild(
        confirmRow(groupLabel(group), choice ? choiceDisplayName(choice) : UNSELECTED)
      );
    });

    // 価格は pricing:"variant" の全軸が揃い、かつ受注可能なときだけ算出
    const variant = CO.findVariant({ productId: product.id, selections: sel });
    const orderable = CO.isVariantAvailable(variant);

    if (variant && orderable) {
      const p = CO.calculatePrices(product, variant);
      setText("co-total", CO.formatYen(p.totalPrice));
      setText("co-deposit", CO.formatYen(p.depositPrice));
      setText("co-balance", CO.formatYen(p.balancePrice));
    } else {
      setText("co-total", "—");
      setText("co-deposit", "—");
      setText("co-balance", "—");
    }

    updateCartButton(sel, variant, orderable);
  }

  // カート追加の活性条件（A-8 / PRD O-7）：
  //   required（既定 true）な select 軸がすべて選択済み ＆ variant 確定 ＆ 受注可能。
  // ダミーデータのうちは条件を満たしても DEMO_MODE で止める。
  // 押せない理由を必ず文章で出す（disabled なボタンだけだと理由が分からない）。
  function updateCartButton(sel, variant, orderable) {
    if (!addCartBtn) return;

    const missing = CO.missingRequiredGroups(product, sel);
    let reason = "";

    if (missing.length) {
      reason = missing.map(groupLabel).join("・") + "を選択してください。";
    } else if (!variant) {
      reason = "この組合せの価格が見つかりません。選択を変更してください。";
    } else if (!orderable) {
      reason = "この組合せは現在お受けできません。選択を変更してください。";
    } else if (!CO.isPriceConsistent(product, variant)) {
      // 表示している前金と実際の請求額がずれている＝管理画面の入力ミス。
      // このまま買わせると請求額が画面と違う事故になるので必ず止める。
      const p = CO.calculatePrices(product, variant);
      console.error(
        "[CustomOrder] 価格の不整合：表示する前金", p.depositPrice,
        "円に対しバリアント価格（実際の請求額）が", p.chargedPrice,
        "円です。合計金額メタフィールドかバリアント価格の入力を確認してください。",
        variant
      );
      reason = "価格の設定に問題があるためご購入いただけません。お問い合わせください。";
    }

    addCartBtn.disabled = !!reason || DEMO_MODE;
    addCartBtn.textContent = DEMO_MODE ? "[デモ] カートに追加" : "カートに追加";

    if (hintEl) {
      hintEl.textContent =
        reason || (DEMO_MODE ? "※ デモ表示のため、カート追加は現在無効です。" : "");
    }
  }

  /* ============================
     Init
  ============================ */
  setText("co-selected-model", product.name);
  renderGroups();
  renderConfirmation();

  if (addCartBtn) {
    addCartBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      if (addCartBtn.disabled) return;

      // 押した瞬間の選択で引き直す。描画時から変わっている可能性があるため
      // （更新漏れの状態でカートに入れると、明細と違うバリアントが売れる）。
      const sel = currentSelections();
      const variant = CO.findVariant({ productId: product.id, selections: sel });

      if (!variant || !CO.isVariantAvailable(variant)) {
        renderConfirmation(); // 理由の表示はここが持っている
        return;
      }

      const prevLabel = addCartBtn.textContent;
      addCartBtn.disabled = true;
      addCartBtn.textContent = "カートに追加中…";
      if (hintEl) hintEl.textContent = "";

      const ok = await CO.addToCart(product, variant, sel);

      if (ok) {
        // 選択内容はカートへ移ったので localStorage は空にする。
        // 残しておくと、戻ってきた顧客が同じ構成をもう一度入れやすい。
        CO.clearState();
        window.location.href = CART_URL;
        return;
      }

      addCartBtn.disabled = false;
      addCartBtn.textContent = prevLabel;
      if (hintEl) {
        hintEl.textContent =
          "カートに追加できませんでした。時間をおいて再度お試しください。";
      }
    });
  }
})();
